import { useEffect, useCallback } from 'react';
import { Template } from '../../../mocks/websiteTemplates';

interface CompareModalProps {
  templateA: Template;
  templateB: Template;
  onClose: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  'E-commerce': 'from-emerald-500 to-teal-500',
  'SaaS & Tech': 'from-violet-500 to-indigo-500',
  'Portfolio & Creative': 'from-pink-500 to-rose-500',
  'Restaurant & Food': 'from-orange-500 to-amber-500',
  'Health & Wellness': 'from-green-500 to-emerald-500',
  'Real Estate': 'from-cyan-500 to-sky-500',
  'Education': 'from-amber-500 to-yellow-500',
  'Non-Profit': 'from-red-500 to-rose-500',
  'Fashion & Beauty': 'from-fuchsia-500 to-pink-500',
  'Travel & Hospitality': 'from-sky-500 to-cyan-500',
  'Finance & Legal': 'from-slate-600 to-gray-600',
  'Entertainment & Media': 'from-purple-500 to-violet-500',
};

const COMPARE_ROWS = [
  { label: 'Category', key: 'category' as const },
  { label: 'Style', key: 'tags' as const },
  { label: 'Description', key: 'description' as const },
  { label: 'Date Added', key: 'dateAdded' as const },
  { label: 'Badge', key: 'badge' as const },
];

const FEATURE_LIST = [
  { icon: 'ri-layout-line', label: 'Responsive Layout' },
  { icon: 'ri-palette-line', label: 'Fully Customizable' },
  { icon: 'ri-seo-line', label: 'SEO Optimized' },
  { icon: 'ri-speed-line', label: 'Fast Load Times' },
  { icon: 'ri-shield-check-line', label: 'Secure & Reliable' },
  { icon: 'ri-device-line', label: 'Mobile-First Design' },
  { icon: 'ri-global-line', label: 'Custom Domain' },
  { icon: 'ri-bar-chart-line', label: 'Analytics Ready' },
];

const TAG_STYLES: Record<string, string> = {
  Minimal: 'bg-gray-100 text-gray-700',
  Elegant: 'bg-rose-50 text-rose-700',
  Bold: 'bg-orange-50 text-orange-700',
  Modern: 'bg-violet-50 text-violet-700',
  Dark: 'bg-gray-800 text-gray-200',
  Colorful: 'bg-pink-50 text-pink-700',
  Warm: 'bg-amber-50 text-amber-700',
  Clean: 'bg-sky-50 text-sky-700',
  Corporate: 'bg-slate-100 text-slate-700',
  Creative: 'bg-fuchsia-50 text-fuchsia-700',
};

interface TemplateColumnProps {
  template: Template;
  side: 'A' | 'B';
}

function TemplateColumn({ template, side }: TemplateColumnProps) {
  const gradient = CATEGORY_COLORS[template.category] ?? 'from-violet-500 to-cyan-500';
  const accent = side === 'A' ? 'ring-violet-400' : 'ring-cyan-400';

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Browser mockup */}
      <div className={`rounded-xl overflow-hidden border-2 ${accent} ring-1 mb-4`}>
        <div className="bg-gray-200 px-3 py-2 flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
          </div>
          <div className="flex-1 bg-white rounded px-2 py-0.5 flex items-center gap-1.5">
            <i className="ri-lock-line text-gray-400 text-xs"></i>
            <span className="text-xs text-gray-500 font-mono truncate">mywebsite.openclaw.ai</span>
          </div>
        </div>
        <div className="w-full" style={{ height: 220 }}>
          <img
            src={template.image}
            alt={template.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* Name + badge */}
      <div className="mb-4">
        <div className="flex items-start gap-2 mb-1">
          <h3 className="text-base font-bold text-gray-900 flex-1 leading-tight">{template.name}</h3>
          {template.badge && (
            <span className={`flex-shrink-0 px-2 py-0.5 text-white text-xs font-semibold rounded-full ${
              template.badge === 'Popular' ? 'bg-amber-500' : 'bg-emerald-500'
            }`}>
              {template.badge}
            </span>
          )}
        </div>
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r ${gradient} text-white text-xs font-medium mb-2`}>
          <i className="ri-folder-line text-xs"></i>
          {template.category}
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">{template.description}</p>
      </div>

      {/* Tags */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Style</p>
        <div className="flex flex-wrap gap-1">
          {template.tags.map((tag) => (
            <span key={tag} className={`px-2 py-0.5 text-xs font-medium rounded-full ${TAG_STYLES[tag] ?? 'bg-gray-100 text-gray-700'}`}>
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Included Features</p>
        <ul className="space-y-1.5">
          {FEATURE_LIST.map(({ icon, label }) => (
            <li key={label} className="flex items-center gap-2 text-xs text-gray-700">
              <div className={`w-5 h-5 flex items-center justify-center rounded-md bg-gradient-to-br ${gradient} opacity-80`}>
                <i className={`${icon} text-white`} style={{ fontSize: 9 }}></i>
              </div>
              <span>{label}</span>
              <i className="ri-check-line ml-auto text-emerald-500 text-sm"></i>
            </li>
          ))}
        </ul>
      </div>

      {/* Date */}
      <p className="text-xs text-gray-400 mb-4">
        <i className="ri-calendar-line mr-1"></i>
        Added {new Date(template.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
      </p>

      {/* CTA */}
      <button className={`w-full py-2.5 rounded-xl bg-gradient-to-r ${gradient} text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap`}>
        <i className="ri-rocket-line"></i>
        Start with {template.name.split(' ').slice(0, 2).join(' ')}
      </button>
    </div>
  );
}

export default function CompareModal({ templateA, templateB, onClose }: CompareModalProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-5xl max-h-[92vh] bg-white rounded-2xl overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <i className="ri-git-compare-line text-white text-sm"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Side-by-Side Comparison</h2>
              <p className="text-xs text-gray-500">Pick the template that fits your vision</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden sm:block">Press Esc to close</span>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Compare body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 flex gap-5 items-start">

            {/* Template A */}
            <TemplateColumn template={templateA} side="A" />

            {/* VS divider */}
            <div className="flex-shrink-0 flex flex-col items-center gap-3 pt-24">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                <span className="text-white text-xs font-black">VS</span>
              </div>
              <div className="w-px flex-1 bg-gradient-to-b from-violet-200 to-cyan-200" style={{ minHeight: 300 }}></div>
            </div>

            {/* Template B */}
            <TemplateColumn template={templateB} side="B" />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-500">All templates include the same powerful features — choose based on style &amp; category</p>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Templates
          </button>
        </div>
      </div>
    </div>
  );
}
